import type { TierHero } from '../types';
import type { ScatterLayout } from './buildScatterPoints';
import { getTierBoundarySampleData } from './tierBoundarySampling';

const BAND_TIERS = ['S', 'A', 'B', 'C', 'D'] as const;
export type BandTier = (typeof BAND_TIERS)[number];

export interface TierBandFillPath {
  tier: BandTier;
  d: string;
}

/** 同じ x サンプル上の上辺・下辺で閉じた帯の path d */
export function closedBandPathD(xs: number[], upperY: number[], lowerY: number[]): string {
  const n = xs.length;
  if (n < 2 || upperY.length !== n || lowerY.length !== n) return '';
  const parts: string[] = [`M ${xs[0]} ${upperY[0]}`];
  for (let i = 1; i < n; i++) {
    parts.push(`L ${xs[i]} ${upperY[i]}`);
  }
  for (let i = n - 1; i >= 0; i--) {
    parts.push(`L ${xs[i]} ${lowerY[i]}`);
  }
  return `${parts.join(' ')} Z`;
}

function safeUpperLower(u: number, l: number): { u: number; l: number } {
  return u <= l ? { u, l } : { u: l, l: u };
}

/**
 * 等ティアスコア境界線の間（＋上端 y=0、下端 y=plotH）を S〜D 色で塗る帯。
 */
export function buildTierBandFillPaths(
  tierHeroes: TierHero[],
  beta: number,
  layout: ScatterLayout
): TierBandFillPath[] {
  const data = getTierBoundarySampleData(tierHeroes, beta, layout);
  if (!data) return [];

  const { xs, boundaryYs, h } = data;
  const n = xs.length;
  const nb = boundaryYs.length;
  if (n < 2) return [];

  const out: TierBandFillPath[] = [];
  const topEdge = () => Array(n).fill(0);
  const bottomEdge = () => Array(n).fill(h);

  for (let b = 0; b <= nb; b++) {
    const upper = b === 0 ? topEdge() : boundaryYs[b - 1]!;
    const lower = b === nb ? bottomEdge() : boundaryYs[b]!;
    const upperY = upper.map((_, i) => safeUpperLower(upper[i]!, lower[i]!).u);
    const lowerY = upper.map((_, i) => safeUpperLower(upper[i]!, lower[i]!).l);
    const d = closedBandPathD(xs, upperY, lowerY);
    if (!d) continue;
    out.push({ tier: BAND_TIERS[b]!, d });
  }
  return out;
}
